import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Drawer, { DrawerBody, DrawerHeader } from '@/design_system/Drawer';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { useConditionForm, useUpdateConditionForm } from '@/models/conditionForm';
import useViewPort from '@/utils/useViewport';

import { ConditionFormEditor, QuestionInput } from './ConditionFormEditor';

export const ConditionFormDialog = ({
  isOpen,
  conditionFormId,
  onClose,
}: {
  isOpen: boolean;
  conditionFormId: string | null;
  onClose: () => void;
}) => {
  const { t } = useLingui();
  const showToast = useShowToast();
  const { isMobile } = useViewPort();

  const { data: conditionFormData, isLoading } = useConditionForm(conditionFormId ?? undefined, {
    enabled: !!conditionFormId && isOpen,
  });

  const { mutateAsync: updateConditionForm, isPending: isUpdatePending } = useUpdateConditionForm();

  const [questions, setQuestions] = useState<QuestionInput[] | undefined>();
  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const handleClose = () => {
    setQuestions(undefined);
    setShowErrors(false);
    setApiError(null);
    onClose();
  };

  const hasUnsavedChanges = questions !== undefined;

  const hasConfigError = (type: string, config: QuestionInput['config']): boolean => {
    if (type === 'multi-quantifiable-select') {
      if (!config || !('addButton' in config)) return true;

      return (
        !config.addButton?.label?.fr ||
        !config.addButton?.label?.en ||
        !config.select?.placeholder?.fr ||
        !config.select?.placeholder?.en
      );
    }
    if (type === 'text-and-select') {
      if (!config || !('input' in config)) return true;
      const options = config.select?.options ?? [];

      return (
        !config.input?.label?.fr ||
        !config.input?.label?.en ||
        !config.input?.placeholder?.fr ||
        !config.input?.placeholder?.en ||
        !config.select?.placeholder?.fr ||
        !config.select?.placeholder?.en ||
        options.length === 0 ||
        options.some((o) => !o.label?.fr || !o.label?.en || !o.value)
      );
    }

    return false;
  };

  const hasError =
    !!questions &&
    questions.some(
      (q) =>
        !q.title.fr ||
        !q.title.en ||
        (q.type !== 'text-and-select' && q.answers.length === 0) ||
        q.answers.some((a) => !a.text.fr || !a.text.en) ||
        hasConfigError(q.type, q.config) ||
        q.answers.some(
          (a) =>
            a.subQuestion &&
            (!a.subQuestion.title.fr ||
              !a.subQuestion.title.en ||
              (a.subQuestion.type !== 'text-and-select' && a.subQuestion.answers.length === 0) ||
              a.subQuestion.answers.some((sa) => !sa.text.fr || !sa.text.en) ||
              hasConfigError(a.subQuestion.type, a.subQuestion.config))
        )
    );

  const onSave = async () => {
    setShowErrors(true);
    setApiError(null);

    if (!questions || hasError) {
      return;
    }

    try {
      if (!conditionFormId) {
        throw new Error('Condition form ID is required');
      }

      await updateConditionForm({
        conditionFormId,
        questions: questions,
      });

      showToast({
        text: (
          <Trans id="settings.condition-forms.update.success">
            Condition form updated successfully
          </Trans>
        ),
      });

      handleClose();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onOpenChange={handleClose}
      closeConfirmation={
        hasUnsavedChanges
          ? {
              title: <Trans id="dialog.discard-changes.title">Discard changes</Trans>,
              content: (
                <Trans id="dialog.discard-changes.content">
                  You're about to leave this page without saving your changes. All unsaved changes
                  will be lost.
                  <br />
                  Are you sure?
                </Trans>
              ),
              confirm: <Trans id="dialog.discard-changes.confirm">Discard changes</Trans>,
            }
          : undefined
      }
    >
      <DrawerHeader>
        <Title size="300">
          <Trans id="settings.condition-forms.edit-title">Edit Condition Form</Trans>
        </Title>
      </DrawerHeader>
      <DrawerBody>
        <Stack padding={isMobile ? '1rem' : '2rem'} gap="1rem">
          {isLoading ? (
            <Paragraph size="200">
              <Trans id="settings.condition-forms.loading">Loading...</Trans>
            </Paragraph>
          ) : (
            <ConditionFormEditor
              conditionForm={conditionFormData}
              questions={questions}
              onChange={(updatedQuestions) => setQuestions(updatedQuestions)}
              showErrors={showErrors}
            />
          )}
        </Stack>
      </DrawerBody>
      <Footer>
        {(apiError || (showErrors && hasError)) && (
          <FooterError>
            {!apiError && showErrors && hasError && (
              <Paragraph weight="medium" color="danger">
                <Trans id="settings.condition-forms.error">Please fill all required fields</Trans>
              </Paragraph>
            )}
            {apiError && (
              <Paragraph weight="medium" color="danger">
                {apiError}
              </Paragraph>
            )}
          </FooterError>
        )}
        <FooterButtons>
          <Button variant="secondary-danger" size="medium" onPress={onClose}>
            <Trans id="settings.condition-forms.cancel">Cancel</Trans>
          </Button>
          <Button variant="primary" size="medium" onPress={onSave} isLoading={isUpdatePending}>
            <Trans id="settings.condition-forms.save">Save</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </Drawer>
  );
};
